import { useCallback, useEffect, useState } from 'react'

import { useSearchParams } from 'react-router-dom'

interface UseUrlParamsProps<T> {
  paramName: string
  defaultValue: T
  parseValue?: (value: string) => T
}
interface UpdateParamOptions {
  extraParam?: {
    name: string
    value: string
  }
  ignoreUpdateParams?: boolean
}
export function useUrlParams<T>({ paramName, defaultValue, parseValue }: UseUrlParamsProps<T>) {
  const [searchParams] = useSearchParams()
  const [value, setValue] = useState<T>(() => {
    const param = searchParams.get(paramName)
    if (!param) return defaultValue
    return parseValue ? parseValue(param) : (param as unknown as T)
  })

  const updateValue = useCallback(
    (newValue: T, options?: UpdateParamOptions) => {
      setValue(newValue)
      const currentParams = new URLSearchParams(window.location.search)
      if (options?.ignoreUpdateParams) return

      if (!newValue) {
        currentParams.delete(paramName)
      } else {
        currentParams.set(paramName, String(newValue))
      }
      if (options?.extraParam) {
        currentParams.set(options?.extraParam?.name, options?.extraParam?.value)
      }
      const newUrl = currentParams.toString()
        ? `${window.location.pathname}?${currentParams.toString()}`
        : window.location.pathname

      window.history.pushState({}, '', newUrl)
    },
    [paramName]
  )

  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search)
      const param = params.get(paramName)
      if (param) {
        setValue(parseValue ? parseValue(param) : (param as unknown as T))
      } else {
        setValue(defaultValue)
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  return [value, updateValue] as const
}
